import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'tips',
  title: 'Air conditioning maintenance tips'
}, {
  id: 'contracts',
  title: 'Commercial AC maintenance contracts'
}, {
  id: 'reports',
  title: 'Sample AC service report'
}, {
  id: 'filters',
  title: 'AC filters: types and cleaning'
}, {
  id: 'winter',
  title: 'Winter AC heating maintenance'
}];
export function MaintenanceGuide() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-946">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-947">
        <div className="container mx-auto px-6" data-id="element-948">
          <div className="max-w-4xl" data-id="element-949">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-950">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-951">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-952" />
              <span className="text-primary-dark" data-id="element-953">Maintenance Guide</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-954">
              Commercial AC maintenance guide
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-955">
              Extend the lifespan of your HVAC systems, reduce energy bills by
              30%, and prevent costly breakdowns with our expert maintenance
              strategies. Published April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-956">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-957">
          <div className="lg:hidden" data-id="element-958">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-959">
              <span data-id="element-960">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-961" /> : <Menu className="w-5 h-5" data-id="element-962" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-963">
                <nav className="flex flex-col gap-3" data-id="element-964">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-965">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-966">
            <div className="sticky top-32" data-id="element-967">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-968">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-969">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-970">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-971" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-972">
            {/* Topic 1 */}
            <section id="tips" className="mb-20 scroll-mt-32" data-id="element-973">
              <h2 className="text-3xl font-serif mb-6" data-id="element-974">
                Air conditioning maintenance tips
              </h2>
              <p data-id="element-975">
                Regular AC maintenance extends system life to 15+ years and can
                save up to 30% on energy consumption. While professional
                servicing is required for commercial use, there are steps
                facility managers can take.
              </p>
              <p data-id="element-976">
                Coventry tips: Schedule major services in spring and autumn to
                avoid peak summer and winter rush periods.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-977">
                Top 10 maintenance tips
              </h3>
              <ol className="list-decimal pl-6 mb-8 space-y-2" data-id="element-978">
                <li data-id="element-979">
                  Replace or clean filters quarterly (or monthly in dusty
                  environments).
                </li>
                <li data-id="element-980">
                  Clear outdoor condenser coils annually to ensure proper
                  airflow.
                </li>
                <li data-id="element-981">
                  Monitor refrigerant levels (must be done by an F-Gas
                  professional).
                </li>
                <li data-id="element-982">
                  Use smart timers to ensure systems are off during non-working
                  hours.
                </li>
                <li data-id="element-983">Test thermostats regularly for accuracy.</li>
                <li data-id="element-984">
                  Keep the area around outdoor units clear of debris and
                  vegetation.
                </li>
                <li data-id="element-985">
                  Check condensate drains for blockages to prevent water damage.
                </li>
                <li data-id="element-986">
                  Listen for unusual noises which indicate failing bearings or
                  belts.
                </li>
                <li data-id="element-987">Inspect ductwork for leaks or poor insulation.</li>
                <li data-id="element-988">
                  Schedule professional preventative maintenance at least twice
                  a year.
                </li>
              </ol>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-989">
                <h3 className="text-2xl font-serif mb-6" data-id="element-990">Maintenance FAQs</h3>
                <div className="space-y-4" data-id="element-991">
                  {[{
                  q: 'What are the signs of poor maintenance?',
                  a: 'High energy bills, weak airflow, foul smells, and inconsistent temperatures are the first signs.'
                }, {
                  q: 'How much does a service cost?',
                  a: 'Commercial servicing in Coventry starts from £100+VAT per visit, depending on system size.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-992">
                      <button onClick={() => setOpenFaqId(openFaqId === `tips-${i}` ? null : `tips-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-993">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-994">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-995">
                          {openFaqId === `tips-${i}` ? <Minus className="w-5 h-5" data-id="element-996" /> : <Plus className="w-5 h-5" data-id="element-997" />}
                        </span>
                      </button>
                      {openFaqId === `tips-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-998">
                          <p itemProp="text" data-id="element-999">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="contracts" className="mb-20 scroll-mt-32" data-id="element-1000">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1001">
                Commercial AC maintenance contracts
              </h2>
              <p data-id="element-1002">
                A formal maintenance contract ensures you meet F-Gas and{' '}
                <Link to="/knowledge-hub/compliance" data-id="element-1003">TM44 compliance</Link>{' '}
                automatically. It shifts the burden of scheduling and
                record-keeping to your HVAC partner.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1004">
                Maintenance packages
              </h3>
              <div className="overflow-x-auto mb-8" data-id="element-1005">
                <table className="w-full text-left border-collapse" data-id="element-1006">
                  <thead data-id="element-1007">
                    <tr className="bg-[#E8F0F8]" data-id="element-1008">
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1009">
                        Package
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1010">
                        Visits/Year
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1011">
                        Free Callouts
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1012">
                        Best For
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1013">
                    <tr data-id="element-1014">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1015">
                        Standard
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1016">2</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1017">
                        None
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1018">
                        Light use retail/small offices
                      </td>
                    </tr>
                    <tr data-id="element-1019">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1020">
                        Premium
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1021">3</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1022">1</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1023">
                        Medium offices, restaurants
                      </td>
                    </tr>
                    <tr data-id="element-1024">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1025">
                        Platinum
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1026">4</td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1027">
                        Unlimited
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1028">
                        Critical environments, server rooms
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1029">
                <h4 className="text-xl font-serif mb-4" data-id="element-1030">
                  Compare maintenance packages
                </h4>
                <p className="mb-6 text-base" data-id="element-1031">
                  Find the right level of cover for your commercial premises.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1032">
                  Get a contract quote
                  <ChevronRight className="w-4 h-4" data-id="element-1033" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1034">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1035">Contract FAQs</h3>
                <div className="space-y-4" data-id="element-1036">
                  {[{
                  q: 'Do contracts include mandatory leak checks?',
                  a: "Yes, all our contracts are tailored to ensure you meet F-Gas leak check regulations based on your system's charge."
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1037">
                      <button onClick={() => setOpenFaqId(openFaqId === `contracts-${i}` ? null : `contracts-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1038">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1039">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1040">
                          {openFaqId === `contracts-${i}` ? <Minus className="w-5 h-5" data-id="element-1041" /> : <Plus className="w-5 h-5" data-id="element-1042" />}
                        </span>
                      </button>
                      {openFaqId === `contracts-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1043">
                          <p itemProp="text" data-id="element-1044">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="reports" className="mb-20 scroll-mt-32" data-id="element-1045">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1046">
                Sample AC service report
              </h2>
              <p data-id="element-1047">
                A high-quality service report is essential for FM directors and
                compliance audits. AccuTemp provides detailed, digitised reports
                after every visit.
              </p>
              <p data-id="element-1048">Our reports include:</p>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1049">
                <li data-id="element-1050">Photos before and after cleaning.</li>
                <li data-id="element-1051">Refrigerant log updates.</li>
                <li data-id="element-1052">Efficiency scores and temperature deltas.</li>
                <li data-id="element-1053">
                  Clear recommendations for future repairs to prevent
                  breakdowns.
                </li>
              </ul>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1054">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1055">Report FAQs</h3>
                <div className="space-y-4" data-id="element-1056">
                  {[{
                  q: 'Can reports be custom branded?',
                  a: 'Yes, for our M&E contractor partners, we offer fully white-labelled service reports.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1057">
                      <button onClick={() => setOpenFaqId(openFaqId === `reports-${i}` ? null : `reports-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1058">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1059">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1060">
                          {openFaqId === `reports-${i}` ? <Minus className="w-5 h-5" data-id="element-1061" /> : <Plus className="w-5 h-5" data-id="element-1062" />}
                        </span>
                      </button>
                      {openFaqId === `reports-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1063">
                          <p itemProp="text" data-id="element-1064">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="filters" className="mb-20 scroll-mt-32" data-id="element-1065">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1066">
                AC filters: types and cleaning
              </h2>
              <p data-id="element-1067">
                Dirty filters are the number one cause of AC breakdowns. They
                restrict airflow, causing the compressor to overwork and
                eventually fail. Cleaning filters monthly can boost airflow by
                up to 25%.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1068">Filter types</h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1069">
                <li data-id="element-1070">
                  <strong data-id="element-1071">Pleated filters:</strong> Better surface area,
                  captures more dust. Standard for commercial units.
                </li>
                <li data-id="element-1072">
                  <strong data-id="element-1073">Pocket filters:</strong> Used in larger AHUs (Air
                  Handling Units) for high-volume filtration.
                </li>
                <li data-id="element-1074">
                  <strong data-id="element-1075">HEPA filters:</strong> Essential for healthcare or
                  environments requiring strict air quality control.
                </li>
              </ul>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1076">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1077">Filter FAQs</h3>
                <div className="space-y-4" data-id="element-1078">
                  {[{
                  q: 'What type of filter is best for allergies?',
                  a: 'HEPA filters are best as they capture 99.97% of particles, including pollen and fine dust.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1079">
                      <button onClick={() => setOpenFaqId(openFaqId === `filters-${i}` ? null : `filters-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1080">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1081">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1082">
                          {openFaqId === `filters-${i}` ? <Minus className="w-5 h-5" data-id="element-1083" /> : <Plus className="w-5 h-5" data-id="element-1084" />}
                        </span>
                      </button>
                      {openFaqId === `filters-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1085">
                          <p itemProp="text" data-id="element-1086">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 5 */}
            <section id="winter" className="mb-20 scroll-mt-32" data-id="element-1087">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1088">
                Winter AC heating maintenance
              </h2>
              <p data-id="element-1089">
                Modern commercial air conditioning systems are heat pumps,
                meaning they provide highly efficient heating in winter.
                However, winter operation puts different stresses on the system.
              </p>
              <p data-id="element-1090">
                Key winter maintenance includes checking the defrost cycle. If
                the outdoor unit cannot properly defrost, ice builds up and can
                crack the fan blades or damage the coil.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1091">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1092">
                  Winter heating FAQs
                </h3>
                <div className="space-y-4" data-id="element-1093">
                  {[{
                  q: 'Are all AC units reversible for heating?',
                  a: 'Approximately 90% of modern commercial AC units are heat pumps capable of both heating and cooling.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1094">
                      <button onClick={() => setOpenFaqId(openFaqId === `winter-${i}` ? null : `winter-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1095">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1096">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1097">
                          {openFaqId === `winter-${i}` ? <Minus className="w-5 h-5" data-id="element-1098" /> : <Plus className="w-5 h-5" data-id="element-1099" />}
                        </span>
                      </button>
                      {openFaqId === `winter-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1100">
                          <p itemProp="text" data-id="element-1101">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}